import React from 'react';
import { AccordionDetails, Button, CircularProgress, Divider, Grid, LinearProgress, Typography } from '@mui/material';
import { ExposureOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderData, PassOut, PassStatus, PassType } from '../../../../api';
import { Accordion, AccordionSummary } from '../../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { UseAuthPermissions } from '../../../../session/UseAuthPermissions';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import { getPunchesDisplay, GuestVisitsLine } from '../../utils';
import { PassDetailPanels } from '../../PassDetailTypes';
import { RedeemedOrderRow } from './RedeemedOrderRow';
import { CanceledOrderRow } from './CanceledOrderRow';

type Props = {
  pass: PassOut;
  expandedPanel: PassDetailPanels | undefined;
  onPanelChange: (panel: PassDetailPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  redeemedOrders: OrderData[];
  canceledOrderDetails: Map<string, OrderData>;
  ordersLoading: boolean;
  onDeleteOrder: (order: OrderData) => void;
  onAdjustPunches: () => void;
};

export const PunchesPanel = ({
  pass,
  expandedPanel,
  onPanelChange,
  redeemedOrders,
  canceledOrderDetails,
  ordersLoading,
  onDeleteOrder,
  onAdjustPunches,
}: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const account = authState.account as unknown as AccountData;
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const punchesUsed = pass.redeemed_order_ids?.length || 0;
  const totalPunches = pass.num_punches_override ?? passOrderPage?.num_punches ?? null;
  const punchProgress = totalPunches != null && totalPunches > 0 ? (punchesUsed / totalPunches) * 100 : 0;
  const punchesDisplay = getPunchesDisplay(pass.redeemed_order_ids, passOrderPage, pass.num_punches_override);
  const isPunchBased = (passOrderPage?.pass_type ?? PassType.PUNCH_BASED) === PassType.PUNCH_BASED;
  const canShowAdjust = isAtLeastSupervisor && isPunchBased && totalPunches != null;
  const isAdjustable = pass.status === PassStatus.ACTIVE || pass.status === PassStatus.SUSPENDED;

  return (
    <Accordion
      expandedPanelName={expandedPanel}
      panelName={PassDetailPanels.Punches}
      onChange={onPanelChange}
      allOpenValue={PassDetailPanels.AllOpen}>
      <AccordionSummary
        title={t('passes.punches')}
        summaryComponent={
          <Typography variant="body2" color="text.secondary">
            {punchesDisplay} {t('passes.used')}
          </Typography>
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          <LinearProgress variant="determinate" value={punchProgress} sx={{ height: 8, borderRadius: 4 }} />
          <Grid container alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap">
            <Typography variant="body2">
              {punchesDisplay} {t('passes.used')}
            </Typography>
            {canShowAdjust && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExposureOutlined />}
                disabled={!isAdjustable}
                onClick={onAdjustPunches}
                sx={{ width: { xs: '100%', md: 'auto' } }}>
                {t('passes.adjust_punches.button')}
              </Button>
            )}
          </Grid>
          {canShowAdjust && !isAdjustable && (
            <Typography variant="caption" color="text.secondary" alignSelf="flex-end">
              {t('passes.adjust_punches.locked')}
            </Typography>
          )}
          <GuestVisitsLine pass={pass} passOrderPage={passOrderPage} />
          {ordersLoading && (
            <Grid container justifyContent="center" py={1}>
              <CircularProgress size={24} />
            </Grid>
          )}
          {!ordersLoading &&
            redeemedOrders.map((order) => <RedeemedOrderRow key={order.id} order={order} onDelete={onDeleteOrder} />)}
          {!ordersLoading && !!pass.canceled_orders?.length && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('passes.canceled_punches')}
              </Typography>
              {pass.canceled_orders.map((co) => (
                <CanceledOrderRow
                  key={co.order_id}
                  canceledOrder={co}
                  orderDetail={canceledOrderDetails.get(co.order_id)}
                />
              ))}
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
